import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  // Global state - sayfa değişse bile korunur
  const [allProducts, setAllProducts] = useState([]);
  const [fullListLoaded, setFullListLoaded] = useState(false);
  const [fullListLoading, setFullListLoading] = useState(false);

  // Ürün adından ölçü çıkar
  const extractOlcu = (urunAdi) => {
    if (!urunAdi) return '';
    const match = urunAdi.match(/(\d+)\s*[xX*]\s*(\d+)/);
    return match ? `${match[1]}x${match[2]}` : '';
  };

  // Tüm listeyi getir (manuel - butona tıklayınca)
  const fetchFullList = useCallback(async () => {
    if (fullListLoaded && allProducts.length > 0) {
      toast.info('Tüm liste zaten yüklü');
      return allProducts;
    }
    
    setFullListLoading(true);
    toast.loading('Tüm ürünler yükleniyor...', { id: 'fulllist' });
    
    try {
      const response = await axios.get(`${API}/panel/urunler?limit=50000`);
      
      if (response.data.success) {
        const urunlerData = response.data.data.map(u => {
          const olcuFromDesc = u.description4 || '';
          const olcuFromName = extractOlcu(u.urun_adi || u.stok_adi || '');
          const finalOlcu = olcuFromDesc || olcuFromName;
          
          return {
            ...u,
            // Stok bilgisi
            stok_bilgisi: { 
              depolar: u.stok_cache?.depolar || u.depolar_stok || {}, 
              toplam: u.stok_cache?.toplam || u.toplam_stok || 0 
            },
            toplam_stok: u.stok_cache?.toplam || u.toplam_stok || 0,
            // Fiyat bilgisi
            liste_fiyati: u.liste_fiyati || u.birim_fiyat || 0,
            birim_fiyat: u.birim_fiyat || u.liste_fiyati || 0,
            fiyat: u.liste_fiyati || u.birim_fiyat || 0,
            olcu: finalOlcu
          };
        });
        
        setAllProducts(urunlerData);
        setFullListLoaded(true);
        
        toast.success(`${response.data.count} ürün yüklendi!`, { id: 'fulllist' });
        return urunlerData;
      }
    } catch (error) {
      toast.error('Tüm liste yüklenemedi', { id: 'fulllist' });
    } finally {
      setFullListLoading(false);
    }
    return [];
  }, [fullListLoaded, allProducts.length]);

  // Ürün stok bilgisini güncelle (arka plan güncelleme için)
  const updateProductStock = useCallback((sku, stockData) => {
    setAllProducts(prev => prev.map(u => 
      u.sku === sku 
        ? { 
            ...u, 
            depolar_stok: stockData.depolar, 
            stok_bilgisi: { depolar: stockData.depolar, toplam: stockData.toplam }, 
            toplam_stok: stockData.toplam,
            // ⚠️ stok_cache KOMPLE değiştirilirse arac/revize/yari_mamul kovaları
            // silinir ve rozetleri kaybolur. Yalnız değişen alanlar yazılır.
            stok_cache: { ...(u.stok_cache || {}),
                          depolar: stockData.depolar, toplam: stockData.toplam }
          }
        : u
    ));
  }, []);

  // Listeyi sıfırla (logout için)
  const resetProductList = useCallback(() => {
    setAllProducts([]);
    setFullListLoaded(false);
    setFullListLoading(false);
  }, []);

  const value = {
    allProducts,
    setAllProducts,
    fullListLoaded,
    setFullListLoaded,
    fullListLoading,
    setFullListLoading,
    fetchFullList,
    updateProductStock,
    resetProductList
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContext;
